import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

// Registered runtime themes. "harmony" is the warm Oracle-ecosystem-aligned skin (docs/26) —
// a skin only: EZ Cloud branding/logo are unchanged in every theme.
export type ThemeName = "light" | "dark" | "harmony";
export const THEMES: ThemeName[] = ["light", "dark", "harmony"];

const isThemeName = (v: unknown): v is ThemeName => THEMES.includes(v as ThemeName);

interface ThemeContextValue {
  /** Active theme name — drives html[data-theme] + the MUI theme object. */
  theme: ThemeName;
  /** Explicit user choice (Appearance selector). Persists + survives logout. */
  setTheme: (theme: ThemeName) => void;
  /**
   * Tenant default (FEATURE_FLAGS.defaultTheme) — applied only while the user has
   * no explicit preference; an explicit choice always wins (docs/26 §3).
   */
  applyTenantDefault: (theme: string | null | undefined) => void;
  /** Back-compat: true only for the dark theme (logo variants, chart configs, …). */
  isDark: boolean;
  /** Back-compat binary flip (light <-> dark). The Appearance UI uses setTheme. */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = "ezcloud_theme";
const STORAGE_EXPLICIT_KEY = "ezcloud_theme_explicit";

// [UAT-UX item 3] The theme preference is a device-level appearance setting, not session state —
// it must survive sign-out and session-expiry storage clears (both do localStorage.clear()).
// Every "clear local storage" path (common/logout.ts, the constants/API interceptor) goes through
// this so the keys live in one place.
export function clearLocalStorageKeepingTheme(): void {
  let theme: string | null = null;
  let explicit: string | null = null;
  try {
    theme = localStorage.getItem(STORAGE_KEY);
    explicit = localStorage.getItem(STORAGE_EXPLICIT_KEY);
  } catch {
    // storage unavailable — nothing to preserve
  }
  localStorage.clear();
  if (explicit === "true" && theme) {
    localStorage.setItem(STORAGE_KEY, theme);
    localStorage.setItem(STORAGE_EXPLICIT_KEY, "true");
  }
}

interface ThemeContextProviderProps {
  children: React.ReactNode;
}

export function ThemeContextProvider({ children }: ThemeContextProviderProps) {
  const [theme, setThemeState] = useState<ThemeName>(() => {
    // Only honour the stored preference if the user explicitly chose it
    if (localStorage.getItem(STORAGE_EXPLICIT_KEY) === "true") {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (isThemeName(stored)) return stored;
    }
    return "light";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const setTheme = useCallback((next: ThemeName) => {
    if (!isThemeName(next)) return;
    localStorage.setItem(STORAGE_EXPLICIT_KEY, "true");
    setThemeState(next);
  }, []);

  // Tenant default (FEATURE_FLAGS.defaultTheme): runtime-only — never writes the explicit
  // flag, so a later explicit user choice (or another tenant's default) still applies.
  const applyTenantDefault = useCallback((tenantDefault: string | null | undefined) => {
    if (!isThemeName(tenantDefault)) return;
    if (localStorage.getItem(STORAGE_EXPLICIT_KEY) === "true") return; // user choice wins
    setThemeState(tenantDefault);
  }, []);

  const toggleTheme = useCallback(() => {
    localStorage.setItem(STORAGE_EXPLICIT_KEY, "true");
    setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider
      value={{ theme, setTheme, applyTenantDefault, isDark: theme === "dark", toggleTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export const useThemeMode = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error("useThemeMode must be used within a ThemeContextProvider");
  }
  return context;
};
